import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Municipio } from '../types';
import clientsService from '../services/clients.service';

interface ClienteFormData {
  nombre: string;
  nit: string;
  tipoEntidad: string;
  telefono: string;
  correo: string;
  direccion: string;
  municipioId: number;
  isActive: boolean;
}

interface ContactoFormData {
  id?: number;
  nombre: string;
  cargo: string;
  correo: string;
  telefono: string;
  principal: boolean;
}

const ClienteForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [filteredMunicipios, setFilteredMunicipios] = useState<Municipio[]>([]);
  const [municipioSearch, setMunicipioSearch] = useState('');
  const [showMunicipioDropdown, setShowMunicipioDropdown] = useState(false);
  const [formData, setFormData] = useState<ClienteFormData>({
    nombre: '',
    nit: '',
    tipoEntidad: 'publico',
    telefono: '',
    correo: '',
    direccion: '',
    municipioId: 0,
    isActive: true,
  });

  const [contactos, setContactos] = useState<ContactoFormData[]>([
    { nombre: '', cargo: 'rector', correo: '', telefono: '', principal: true },
    { nombre: '', cargo: 'secretario', correo: '', telefono: '', principal: false },
    { nombre: '', cargo: 'auxiliar', correo: '', telefono: '', principal: false },
  ]);

  useEffect(() => {
    const fetchCliente = async () => {
      try {
        const municipiosData = await clientsService.getMunicipios();

        // Normaliza propiedades y tipos
        const munNorm = municipiosData.map((m: any) => ({
          ...m,
          id: Number(m.id ?? m.municipioId),
          departamentoId: Number(m.departamentoId ?? m.departamento_id),
          displayName: `${m.nombre} - ${m.departamento?.nombre || 'Sin departamento'}`
        }));

        setMunicipios(munNorm);
        setFilteredMunicipios(munNorm);
        
        // Nota: municipioSearch se establece más abajo una vez se carga el cliente

        if (isEditing && id) {
          const c = await clientsService.getClienteById(parseInt(id));
          const selectedMunicipio = munNorm.find(m => m.id === Number(c.municipioId));
          setFormData(prev => ({
            ...prev,
            nombre: c.nombre,
            nit: c.nit,
            tipoEntidad: c.tipoEntidad,
            telefono: c.telefono ?? '',
            correo: c.correo ?? '',
            direccion: c.direccion ?? '',
            municipioId: Number(c.municipioId ?? 0),
            isActive: c.isActive ?? true,
          }));
          
          if (selectedMunicipio) {
            setMunicipioSearch(selectedMunicipio.displayName);
          }

          // Cargar contactos existentes y completar con espacios vacíos
          const contactosExistentes = c.contactos || [];
          console.log('Contactos encontrados en el cliente:', contactosExistentes);
          
          // Definir los tres tipos de contacto requeridos
          const tiposContacto = [
            { cargo: 'rector', principal: true },
            { cargo: 'secretario', principal: false },
            { cargo: 'auxiliar', principal: false }
          ];
          
          // Crear array de contactos completos
          const contactosCompletos = tiposContacto.map(tipo => {
            // Buscar si existe un contacto con este cargo
            const contactoExistente = contactosExistentes.find(c => c.cargo === tipo.cargo);
            
            if (contactoExistente) {
              // Si existe, usar los datos existentes
              return {
                id: contactoExistente.id,
                nombre: contactoExistente.nombre,
                cargo: contactoExistente.cargo || tipo.cargo,
                correo: contactoExistente.correo || '',
                telefono: contactoExistente.telefono || '',
                principal: contactoExistente.principal
              };
            } else {
              // Si no existe, crear un espacio vacío
              return {
                nombre: '',
                cargo: tipo.cargo,
                correo: '',
                telefono: '',
                principal: tipo.principal
              };
            }
          });
          
          console.log('Contactos completos procesados:', contactosCompletos);
          setContactos(contactosCompletos);
        }
      } catch (e) {
        console.error('Error fetching data:', e);
        alert('Error al cargar los datos');
      }
    };

    fetchCliente();
  }, [id, isEditing]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('.municipio-dropdown')) {
        setShowMunicipioDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'municipioId' ? parseInt(value) : value
    }));
  };

  const handleMunicipioSearch = (value: string) => {
    setMunicipioSearch(value);
    setShowMunicipioDropdown(true);
    
    if (value.trim() === '') {
      setFilteredMunicipios(municipios);
    } else {
      const filtered = municipios.filter(municipio => 
        municipio.displayName?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredMunicipios(filtered);
    }
  };

  const handleMunicipioSelect = (municipio: any) => {
    setFormData(prev => ({ ...prev, municipioId: municipio.id }));
    setMunicipioSearch(municipio.displayName);
    setShowMunicipioDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.municipioId === 0 || !municipios.find(m => m.id === formData.municipioId)) {
      alert('Por favor, selecciona un municipio válido de la lista.');
      return;
    }
    setLoading(true);

    try {
      let cliente;
      if (isEditing && id) {
        cliente = await clientsService.updateCliente(parseInt(id), formData);
      } else {
        // Para crear un nuevo cliente, no enviar isActive (se establecerá como true por defecto)
        const { isActive, ...createData } = formData;
        cliente = await clientsService.createCliente(createData);
      }
      
      // Guardar/actualizar contactos
      for (const contacto of contactos) {
        if (contacto.nombre.trim()) {
          const contactoData = {
            nombre: contacto.nombre,
            cargo: contacto.cargo,
            correo: contacto.correo || undefined,
            telefono: contacto.telefono || undefined,
            principal: contacto.principal
          };

          console.log('Procesando contacto:', { id: contacto.id, nombre: contacto.nombre, cargo: contacto.cargo });

          if (contacto.id) {
            // Actualizar contacto existente
            console.log('Actualizando contacto existente con ID:', contacto.id);
            await clientsService.updateContacto(contacto.id, contactoData);
          } else {
            // Crear nuevo contacto
            console.log('Creando nuevo contacto');
            const newContactoData = {
              ...contactoData,
              clienteId: cliente.id
            };
            await clientsService.createContacto(newContactoData);
          }
        }
      }
      
      navigate('/clientes');
    } catch (error) {
      console.error('Error saving cliente:', error);
      alert('Error al guardar el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/clientes')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a clientes
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Actualiza la información del cliente' : 'Registra un nuevo cliente en el sistema'}
        </p>
      </div>

      {/* Form */}
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Información Básica */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Información Básica
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Institución *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  required
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="nit" className="block text-sm font-medium text-gray-700 mb-2">
                  NIT *
                </label>
                <input
                  type="text"
                  id="nit"
                  name="nit"
                  required
                  value={formData.nit}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="tipoEntidad" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Entidad *
                </label>
                <select
                  id="tipoEntidad"
                  name="tipoEntidad"
                  required
                  value={formData.tipoEntidad}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="publico">Público</option>
                  <option value="privado">Privado</option>
                </select>
              </div>

              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="correo" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="correo"
                  name="correo"
                  value={formData.correo}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="direccion" className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección
                </label>
                <input
                  type="text"
                  id="direccion"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="mt-6 relative municipio-dropdown">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Municipio *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={municipioSearch}
                  onChange={(e) => handleMunicipioSearch(e.target.value)}
                  onFocus={() => setShowMunicipioDropdown(true)}
                  placeholder="Buscar municipio..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  autoComplete="off"
                />
                {showMunicipioDropdown && filteredMunicipios.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto mt-1">
                    {filteredMunicipios.map((municipio) => (
                      <div
                        key={municipio.id}
                        onClick={() => handleMunicipioSelect(municipio)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                      >
                        {municipio.displayName}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {municipioSearch && !municipios.find(m => m.displayName === municipioSearch) && (
                <p className="text-red-500 text-xs mt-1">Selecciona un municipio válido de la lista</p>
              )}
            </div>

            {/* Estado del Cliente - Solo en modo edición */}
            {isEditing && (
              <div className="mt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Cliente activo
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Los clientes inactivos no aparecerán en las listas principales
                </p>
              </div>
            )}
          </div>

          {/* Información de Contactos */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Información de Contactos
            </h2>

            {contactos.map((contacto, index) => (
              <div key={contacto.id || `new-${index}`} className="mb-8 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-md font-medium text-gray-800 mb-4 capitalize">
                  {contacto.cargo === 'rector' ? 'Rector' : 
                   contacto.cargo === 'secretario' ? 'Secretario' : 
                   'Auxiliar Administrativo'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={contacto.nombre}
                      onChange={(e) => {
                        const newContactos = [...contactos];
                        newContactos[index].nombre = e.target.value;
                        setContactos(newContactos);
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={contacto.telefono}
                      onChange={(e) => {
                        const newContactos = [...contactos];
                        newContactos[index].telefono = e.target.value;
                        setContactos(newContactos);
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={contacto.correo}
                      onChange={(e) => {
                        const newContactos = [...contactos];
                        newContactos[index].correo = e.target.value;
                        setContactos(newContactos);
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/clientes')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Cliente')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClienteForm;